<template>
	<section class="news-release-section" :aria-label="t(`${I18N_NS}.newsRelease.ariaLabel`)">
		<div class="pc-center-1200">
			<div class="news-release-section__panel">
				<header class="news-release-section__header">
					<div class="news-release-section__title-row">
						<img
							class="news-release-section__decor"
							src="../../images/title-decor-left.png"
							alt=""
							aria-hidden="true"
						/>
						<h2 class="news-release-section__title">{{ t(`${I18N_NS}.newsRelease.title`) }}</h2>
						<img
							class="news-release-section__decor"
							src="../../images/title-decor-right.png"
							alt=""
							aria-hidden="true"
						/>
					</div>
					<p class="news-release-section__subtitle">
						{{ t(`${I18N_NS}.newsRelease.subtitle`) }}
					</p>
				</header>

				<div class="news-release-section__body">
					<div class="news-release-section__grid">
						<template v-if="loading">
							<article
								v-for="item in pageSize"
								:key="item"
								class="news-card news-card--skeleton"
								aria-hidden="true"
							>
								<span class="news-card__skeleton-image"></span>
								<div class="news-card__content">
									<span class="news-card__skeleton-line news-card__skeleton-line--title"></span>
									<span class="news-card__skeleton-line news-card__skeleton-line--date"></span>
								</div>
							</article>
						</template>
						<a
							v-for="(item, index) in currentList"
							v-else
							:key="getNewsId(item) || item.title"
							:ref="el => setNewsCardRef(el, item)"
							class="news-card"
							:href="item.link"
							target="_blank"
							rel="noopener noreferrer"
							:aria-label="t(`${I18N_NS}.newsRelease.viewNewsAria`, { title: item.title })"
							@click="handleCardClick(item, index)"
						>
							<figure class="news-card__image">
								<img :src="item.banner" :alt="item.title" />
								<!-- <span class="news-card__tag">{{ t(`${I18N_NS}.newsRelease.tag`) }}</span> -->
							</figure>
							<div class="news-card__content">
								<h3 class="news-card__title">{{ item.title }}</h3>
								<p class="news-card__date">
									<img
										class="news-card__date-icon"
										src="../../images/icon-calendar.png"
										alt=""
										aria-hidden="true"
									/>
									<span>{{ item.date }}</span>
								</p>
							</div>
						</a>
					</div>

					<nav
						v-if="totalPages > 1"
						class="news-release-section__pagination"
						:aria-label="t(`${I18N_NS}.newsRelease.paginationAria`)"
					>
						<button
							type="button"
							class="page-button page-arrow"
							:disabled="currentPage === 1"
							:aria-label="t(`${I18N_NS}.newsRelease.prevPage`)"
							@click="changePage(currentPage - 1, 'prev')"
						>
							&lt;
						</button>

						<button
							v-for="page in totalPages"
							:key="page"
							type="button"
							class="page-button"
							:class="{ active: page === currentPage }"
							:aria-current="page === currentPage ? 'page' : undefined"
							@click="changePage(page, 'number')"
						>
							{{ page }}
						</button>

						<button
							type="button"
							class="page-button page-arrow"
							:disabled="currentPage === totalPages"
							:aria-label="t(`${I18N_NS}.newsRelease.nextPage`)"
							@click="changePage(currentPage + 1, 'next')"
						>
							&gt;
						</button>
					</nav>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "~/composables/useI18n";
import {
	getNewsId,
	truncateTitle,
	useAboutUsTracking,
} from "../../../composables/useAboutUsTracking";

const I18N_NS = "aboutUs";
const { t } = useI18n();
const { track } = useAboutUsTracking("pc");

const props = defineProps({
	loading: {
		type: Boolean,
		default: false,
	},
	list: {
		type: Array,
		default: () => [],
	},
	pageSize: {
		type: Number,
		default: 6,
	},
});

const currentPage = ref(1);
const newsCardRefs = new Map();
const impressedNewsIds = new Set();
const impressionTimers = new Map();
let newsObserver = null;

const totalPages = computed(() => Math.max(1, Math.ceil(props.list.length / props.pageSize)));
const currentList = computed(() => {
	const start = (currentPage.value - 1) * props.pageSize;
	return props.list.slice(start, start + props.pageSize);
});

const getGlobalPosition = (item, localIndex) => {
	const globalIndex = props.list.findIndex(
		news => getNewsId(news) === getNewsId(item) && news.title === item.title
	);
	return globalIndex >= 0
		? globalIndex + 1
		: (currentPage.value - 1) * props.pageSize + localIndex + 1;
};

const getNewsParams = (item, localIndex) => ({
	news_id: getNewsId(item),
	news_title: truncateTitle(item.title),
	position: getGlobalPosition(item, localIndex),
	news_date: item.date,
});

const handleCardClick = (item, index) => {
	track("news_card_click", getNewsParams(item, index));
};

const changePage = (page, direction) => {
	if (page < 1 || page > totalPages.value || page === currentPage.value) {
		return;
	}

	track("news_pagination_click", {
		page_index: page,
		direction,
		current_page: currentPage.value,
	});
	currentPage.value = page;
};

const setNewsCardRef = (el, item) => {
	const id = getNewsId(item);
	if (!id) return;
	if (el) {
		newsCardRefs.set(id, el);
	} else {
		newsCardRefs.delete(id);
	}
};

function refreshNewsObserver() {
	if (!import.meta.client) return;
	newsObserver?.disconnect();

	newsObserver = new IntersectionObserver(
		entries => {
			entries.forEach(entry => {
				const id = entry.target.dataset.newsId;
				if (!id || impressedNewsIds.has(id)) return;

				if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
					if (impressionTimers.has(id)) return;
					const timer = window.setTimeout(() => {
						const item = currentList.value.find(news => getNewsId(news) === id);
						const localIndex = currentList.value.findIndex(news => getNewsId(news) === id);
						if (!item || impressedNewsIds.has(id) || localIndex < 0) return;
						impressedNewsIds.add(id);
						track("news_card_impression", getNewsParams(item, localIndex));
					}, 1000);
					impressionTimers.set(id, timer);
				} else if (impressionTimers.has(id)) {
					window.clearTimeout(impressionTimers.get(id));
					impressionTimers.delete(id);
				}
			});
		},
		{ threshold: [0.5] }
	);

	currentList.value.forEach(item => {
		const id = getNewsId(item);
		const el = newsCardRefs.get(id);
		if (!el) return;
		el.dataset.newsId = id;
		newsObserver.observe(el);
	});
}

onMounted(() => {
	nextTick(refreshNewsObserver);
});

watch(
	() => props.list,
	() => {
		currentPage.value = 1;
		nextTick(refreshNewsObserver);
	}
);

watch(currentList, () => {
	nextTick(refreshNewsObserver);
});

onBeforeUnmount(() => {
	impressionTimers.forEach(timer => window.clearTimeout(timer));
	impressionTimers.clear();
	newsObserver?.disconnect();
});
</script>

<style lang="scss" scoped>
.news-release-section {
	padding: 0 0 59px;
	background: #ffffff;

	&__panel {
		padding: 59px 0px 0px;
	}

	&__header {
		margin-bottom: 40px;
		text-align: center;
	}

	&__title-row {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 16px;
		margin-bottom: 12px;
	}

	&__decor {
		width: 120px;
		height: 16px;
		object-fit: contain;
		flex-shrink: 0;
	}

	&__title {
		margin: 0;
		color: #333333;
		font-size: 42px;
		font-weight: 700;
		line-height: 60px;
		white-space: nowrap;
	}

	&__subtitle {
		margin: 0;
		color: #333333;
		font-size: 32px;
		line-height: 46px;
	}

	&__body {
		padding: 30px;
		background: #ffffff;
		border: 1px solid #efefef;
		border-radius: 20px;
	}

	&__grid {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 20px;
	}

	&__pagination {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 8px;
		margin-top: 32px;
	}
}

.page-button {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	border: 1px solid #e5e5e5;
	border-radius: 8px;
	background: #ffffff;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: #333333;
	cursor: pointer;
	transition:
		background-color 0.2s ease,
		border-color 0.2s ease,
		color 0.2s ease;

	&:hover:not(:disabled):not(.active):not(.page-arrow) {
		border-color: #333333;
		background: #f7f7f7;
		color: #333333;
	}

	&.active {
		border-color: #000000;
		background: #000000;
		color: #ffffff;
	}

	&:disabled {
		cursor: not-allowed;
	}
}

.page-arrow {
	border: none;
	background: #f2f2f2;
	color: #cccccc;
	font-size: 16px;

	&:hover:not(:disabled) {
		background: #e5e5e5;
		color: #666666;
	}

	&:disabled {
		background: #f2f2f2;
		color: #e0e0e0;
	}
}

.news-card {
	display: flex;
	flex-direction: column;
	min-height: 360px;
	border: 1px solid #ebe5dc;
	border-radius: 12px;
	background: #ffffff;
	overflow: hidden;
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease,
		border-color 0.2s ease;

	&:hover {
		transform: translateY(-4px);
		border-color: rgba(198, 127, 52, 0.28);
		box-shadow: 0 12px 32px rgba(17, 17, 17, 0.08);

		.news-card__title {
			color: #f2af19;
		}
	}

	&__image {
		position: relative;
		margin: 0;
		height: 236px;
		overflow: hidden;
		background: #f4f4f4;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}
	}

	&__tag {
		position: absolute;
		top: 10px;
		right: 20px;
		padding: 4px 6px;
		border-radius: 2px;
		background: #f2af19;
		color: #ffffff;
		font-size: 14px;
		font-weight: 500;
		line-height: 1;
		white-space: nowrap;
	}

	&__content {
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: 18px 23px 18px;
	}

	&__title {
		margin: 0;
		font-size: 22px;
		line-height: 35px;
		font-weight: 700;
		color: #333333;
		transition: color 0.2s ease;
		display: -webkit-box;
		-webkit-line-clamp: 3;
		line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	&__date {
		display: flex;
		align-items: center;
		gap: 9px;
		margin: auto 0 0;
		padding-top: 16px;
		font-size: 18px;
		line-height: 1.5;
		color: #a0a0a0;
	}

	&__date-icon {
		width: 15px;
		height: 15px;
		flex-shrink: 0;
	}
}

@media (max-width: 1024px) {
	.news-release-section {
		&__body {
			padding: 24px 20px;
		}

		&__grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: 16px;
		}

		&__title {
			font-size: 36px;
			line-height: 1.4;
		}

		&__subtitle {
			font-size: 26px;
			line-height: 1.4;
		}
	}

	.news-card {
		min-height: 320px;

		&__image {
			height: 200px;
		}

		&__title {
			font-size: 20px;
			line-height: 1.5;
		}
	}
}

@media (max-width: 768px) {
	.news-release-section {
		padding-bottom: 40px;

		&__panel {
			padding-top: 40px;
		}

		&__decor {
			width: clamp(48px, 16vw, 80px);
			height: 12px;
		}

		&__title-row {
			gap: 10px;
		}

		&__title {
			white-space: normal;
			font-size: 28px;
		}

		&__subtitle {
			font-size: 20px;
		}

		&__body {
			padding: 20px 16px;
			border-radius: 16px;
		}

		&__grid {
			grid-template-columns: minmax(0, 1fr);
		}
	}

	.news-card {
		min-height: auto;

		&__content {
			padding: 16px;
		}

		&__date {
			font-size: 16px;
		}
	}
}

.news-card--skeleton {
	pointer-events: none;
}
.news-card__skeleton-image,
.news-card__skeleton-line {
	display: block;
	background: linear-gradient(90deg, #eee 25%, #fafafa 42%, #eee 62%);
	background-size: 300% 100%;
	animation: about-news-shimmer 1.35s ease-in-out infinite;
}
.news-card__skeleton-image {
	width: 100%;
	aspect-ratio: 16/9;
}
.news-card__skeleton-line {
	height: 18px;
	border-radius: 5px;
	margin-bottom: 18px;
}
.news-card__skeleton-line--title {
	width: 86%;
	height: 24px;
}
.news-card__skeleton-line--date {
	width: 38%;
	margin-top: auto;
	margin-bottom: 0;
}
@keyframes about-news-shimmer {
	to {
		background-position: -100% 0;
	}
}
</style>
